import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type { CommSettings } from '@/types/communications'
import {
    Badge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Field,
    Input,
    LoadingPanel,
    PillButton,
    SectionTitle,
    Toggle,
} from './_shared'
import {
    Mail,
    Bell,
    Smartphone,
    Save,
    Check,
    ShieldAlert,
    Clock,
    Gauge,
} from 'lucide-react'

export function SettingsTab() {
    const [settings, setSettings] = useState<CommSettings | null>(null)
    const [draft, setDraft] = useState<Partial<CommSettings> | null>(null)
    const [saving, setSaving] = useState(false)
    const [savedAt, setSavedAt] = useState<number | null>(null)

    useEffect(() => {
        adminApi.getCommSettings().then(s => {
            setSettings(s)
            setDraft(s)
        })
    }, [])

    if (!draft || !settings) {
        return <LoadingPanel label="Loading settings…" />
    }

    const dirty = JSON.stringify(draft) !== JSON.stringify(settings)

    const setConfig = (path: string[], value: unknown) => {
        setDraft(prev => {
            if (!prev) return prev
            const next = { ...prev, config: { ...(prev.config ?? {}) } }
            let cursor: Record<string, unknown> = next.config!
            for (let i = 0; i < path.length - 1; i++) {
                const key = path[i]
                cursor[key] = { ...((cursor[key] as Record<string, unknown>) ?? {}) }
                cursor = cursor[key] as Record<string, unknown>
            }
            cursor[path[path.length - 1]] = value
            return next
        })
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const updated = await adminApi.updateCommSettings(draft)
            setSettings(updated)
            setDraft(updated)
            setSavedAt(Date.now())
        } finally {
            setSaving(false)
        }
    }

    const caps = (draft.config?.caps ?? {}) as {
        promotionalPerWeek?: number
        transactionalPerDay?: number
    }
    const windows = (draft.config?.windows ?? {}) as {
        timezone?: string
        startHour?: number
        endHour?: number
    }

    return (
        <div className="max-w-4xl space-y-6">
            <SectionTitle
                title="Global settings"
                description="Org-wide kill-switches and limits. These apply on top of user-level preferences — if a channel is off here, no one receives anything on it."
                actions={
                    <>
                        {savedAt && !dirty && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                                <Check className="h-3 w-3 text-green-600" /> Saved
                            </span>
                        )}
                        <PillButton
                            variant="primary"
                            onClick={handleSave}
                            disabled={!dirty || saving}
                        >
                            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
                        </PillButton>
                    </>
                }
            />

            <Card>
                <CardHeader>
                    <CardTitle className="!text-sm">
                        <span className="inline-flex items-center gap-2">
                            <ShieldAlert className="h-4 w-4 text-blue-600" />
                            Channel kill-switches
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Toggle
                        checked={draft.emailEnabled ?? true}
                        onChange={v => setDraft({ ...draft, emailEnabled: v })}
                        label="Email"
                        description="Master switch for all outbound email."
                    />
                    <Toggle
                        checked={draft.pushEnabled ?? true}
                        onChange={v => setDraft({ ...draft, pushEnabled: v })}
                        label="Push notifications"
                        description="Web push via Service Worker."
                    />
                    <Toggle
                        checked={draft.inAppEnabled ?? true}
                        onChange={v => setDraft({ ...draft, inAppEnabled: v })}
                        label="In-app notifications"
                        description="The bell icon + toast notifications."
                    />
                    <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-3">
                        <ChannelStatus
                            icon={Mail}
                            label="Email"
                            enabled={draft.emailEnabled ?? true}
                        />
                        <ChannelStatus
                            icon={Smartphone}
                            label="Push"
                            enabled={draft.pushEnabled ?? true}
                        />
                        <ChannelStatus
                            icon={Bell}
                            label="In-app"
                            enabled={draft.inAppEnabled ?? true}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="!text-sm">
                        <span className="inline-flex items-center gap-2">
                            <Gauge className="h-4 w-4 text-blue-600" />
                            Frequency caps
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-4 text-xs text-gray-500">
                        Hard caps applied across the engine. Set to{' '}
                        <code className="font-mono">0</code> to disable a cap.
                    </p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field
                            label="Promotional emails per week"
                            hint="Lifecycle, broadcast, digest. Transactional sends bypass."
                        >
                            <Input
                                type="number"
                                value={caps.promotionalPerWeek ?? 1}
                                onChange={e =>
                                    setConfig(
                                        ['caps', 'promotionalPerWeek'],
                                        Number(e.target.value)
                                    )
                                }
                            />
                        </Field>
                        <Field
                            label="Transactional emails per day"
                            hint="0 = uncapped. Use sparingly — transactional sends are user-initiated."
                        >
                            <Input
                                type="number"
                                value={caps.transactionalPerDay ?? 0}
                                onChange={e =>
                                    setConfig(
                                        ['caps', 'transactionalPerDay'],
                                        Number(e.target.value)
                                    )
                                }
                            />
                        </Field>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="!text-sm">
                        <span className="inline-flex items-center gap-2">
                            <Clock className="h-4 w-4 text-blue-600" />
                            Allowed send window
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-4 text-xs text-gray-500">
                        Lifecycle and promotional sends will be deferred to the next allowed
                        window in the user's timezone (or this default if unset).
                    </p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <Field label="Default timezone">
                            <Input
                                value={windows.timezone ?? 'UTC'}
                                onChange={e =>
                                    setConfig(['windows', 'timezone'], e.target.value)
                                }
                                placeholder="UTC"
                            />
                        </Field>
                        <Field label="Start hour (0–23)">
                            <Input
                                type="number"
                                value={windows.startHour ?? 9}
                                onChange={e =>
                                    setConfig(
                                        ['windows', 'startHour'],
                                        Math.max(0, Math.min(23, Number(e.target.value)))
                                    )
                                }
                            />
                        </Field>
                        <Field label="End hour (0–23)">
                            <Input
                                type="number"
                                value={windows.endHour ?? 20}
                                onChange={e =>
                                    setConfig(
                                        ['windows', 'endHour'],
                                        Math.max(0, Math.min(23, Number(e.target.value)))
                                    )
                                }
                            />
                        </Field>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

function ChannelStatus({
    icon: Icon,
    label,
    enabled,
}: {
    icon: React.ComponentType<{ className?: string }>
    label: string
    enabled: boolean
}) {
    return (
        <div
            className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                enabled
                    ? 'border-gray-200 bg-gray-50 text-gray-900'
                    : 'border-red-200 bg-red-50 text-red-900'
            }`}
        >
            <Icon
                className={`h-5 w-5 ${enabled ? 'text-blue-600' : 'text-red-600'}`}
            />
            <div className="flex-1">
                <div className="text-sm font-medium">{label}</div>
                <Badge variant={enabled ? 'success' : 'error'}>
                    {enabled ? 'live' : 'paused'}
                </Badge>
            </div>
        </div>
    )
}
